import { useEffect } from "react";
import { Link } from "react-router-dom";

import { LanguageSwitcher } from "../../components/layout/LanguageSwitcher";
import { t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

import "./privacy.css";

type PrivacySection = {
  title: string;
  body: string;
  items?: string[];
};

const sections: PrivacySection[] = [
  {
    title: "privacy.sections.scope.title",
    body: "privacy.sections.scope.body",
  },
  {
    title: "privacy.sections.collection.title",
    body: "privacy.sections.collection.body",
    items: [
      "privacy.sections.collection.items.identity",
      "privacy.sections.collection.items.device",
      "privacy.sections.collection.items.protection",
      "privacy.sections.collection.items.exclusions",
      "privacy.sections.collection.items.requests",
    ],
  },
  {
    title: "privacy.sections.use.title",
    body: "privacy.sections.use.body",
    items: [
      "privacy.sections.use.items.enrollment",
      "privacy.sections.use.items.enforcement",
      "privacy.sections.use.items.operations",
      "privacy.sections.use.items.security",
    ],
  },
  {
    title: "privacy.sections.network.title",
    body: "privacy.sections.network.body",
  },
  {
    title: "privacy.sections.excluded.title",
    body: "privacy.sections.excluded.body",
  },
  {
    title: "privacy.sections.sharing.title",
    body: "privacy.sections.sharing.body",
  },
  {
    title: "privacy.sections.retention.title",
    body: "privacy.sections.retention.body",
    items: [
      "privacy.sections.retention.items.raw",
      "privacy.sections.retention.items.aggregate",
      "privacy.sections.retention.items.audit",
      "privacy.sections.retention.items.account",
    ],
  },
  {
    title: "privacy.sections.security.title",
    body: "privacy.sections.security.body",
  },
  {
    title: "privacy.sections.rights.title",
    body: "privacy.sections.rights.body",
  },
  {
    title: "privacy.sections.children.title",
    body: "privacy.sections.children.body",
  },
  {
    title: "privacy.sections.changes.title",
    body: "privacy.sections.changes.body",
  },
];

export function PrivacyPage() {
  const [locale] = useLocale();

  useEffect(() => {
    document.title = t("privacy.documentTitle");
  }, [locale]);

  return (
    <div className="privacy-page">
      <header className="privacy-nav">
        <Link className="privacy-brand" to="/" aria-label={t("privacy.homeLabel")}>
          <span className="privacy-brand__mark" aria-hidden="true">O</span>
          <span>OdBlox</span>
        </Link>
        <div className="privacy-nav__actions">
          <LanguageSwitcher />
          <Link className="privacy-login" to="/login">{t("privacy.adminLogin")}</Link>
        </div>
      </header>

      <main>
        <section className="privacy-hero">
          <div className="privacy-hero__glow" aria-hidden="true" />
          <div className="privacy-hero__content">
            <p className="privacy-eyebrow">{t("privacy.eyebrow")}</p>
            <h1>{t("privacy.title")}</h1>
            <p className="privacy-lead">{t("privacy.lead")}</p>
            <div className="privacy-effective">
              <span>{t("privacy.effectiveLabel")}</span>
              <strong>{t("privacy.effectiveDate")}</strong>
            </div>
          </div>
        </section>

        <div className="privacy-layout">
          <aside className="privacy-summary">
            <p className="privacy-summary__label">{t("privacy.promise.label")}</p>
            <p className="privacy-summary__statement">{t("privacy.promise.statement")}</p>
            <div className="privacy-summary__rule" />
            <p>{t("privacy.promise.detail")}</p>
          </aside>

          <article className="privacy-content">
            {sections.map((section, index) => (
              <section className="privacy-section" key={section.title}>
                <span className="privacy-section__number" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <h2>{t(section.title)}</h2>
                  <p>{t(section.body)}</p>
                  {section.items && (
                    <ul>
                      {section.items.map((item) => <li key={item}>{t(item)}</li>)}
                    </ul>
                  )}
                </div>
              </section>
            ))}

            <section className="privacy-contact">
              <p className="privacy-eyebrow">{t("privacy.contact.eyebrow")}</p>
              <h2>{t("privacy.contact.title")}</h2>
              <p>{t("privacy.contact.body")}</p>
              <a href="mailto:app@takom.com.tr">app@takom.com.tr</a>
            </section>
          </article>
        </div>
      </main>

      <footer className="privacy-footer">
        <span>OdBlox</span>
        <span>{t("privacy.footer")}</span>
      </footer>
    </div>
  );
}
